import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="group relative bg-card rounded-xl overflow-hidden shadow-elegant hover:shadow-elevated transition-all duration-500 transform hover:-translate-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative h-64 overflow-hidden">
        <Image
          src={project?.image}
          alt={project?.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/50 to-transparent transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="ExternalLink" size={16} className="text-white" />
                <span className="text-white text-sm font-medium">View Live</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Github" size={16} className="text-white" />
                <span className="text-white text-sm font-medium">Source</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {project?.status && (
          <div className="absolute top-4 right-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              project?.status === 'Live' ?'bg-success text-white' 
                : project?.status === 'In Development' ?'bg-warning text-white' :'bg-muted text-muted-foreground'
            }`}>
              {project?.status}
            </span>
          </div>
        )}
      </div>
      {/* Project Content */}
      <div className="p-6">
        {/* Category & Date */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-primary text-sm font-medium">{project?.category}</span>
          <span className="text-muted-foreground text-sm">{project?.year}</span>
        </div>

        {/* Title & Description */}
        <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
          {project?.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {project?.description}
        </p>

        {/* Tech Stack */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project?.technologies?.slice(0, 4)?.map((tech, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
            >
              {tech}
            </span>
          ))}
          {project?.technologies?.length > 4 && (
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
              +{project?.technologies?.length - 4} more
            </span>
          )}
        </div>

        {/* Metrics */}
        {project?.metrics && (
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
            {Object.entries(project?.metrics)?.slice(0, 2)?.map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="text-lg font-bold text-primary">{value}</div>
                <div className="text-xs text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')}</div>
              </div>
            ))}
          </div>
        )}

        {/* Action Button */}
        <Button
          variant="outline"
          fullWidth
          onClick={() => onViewDetails(project)}
          iconName="ArrowRight"
          iconPosition="right"
          className="group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-300"
        >
          View Case Study
        </Button>
      </div>
    </div>
  );
};

export default ProjectCard;